import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { Button, Checkbox, TextInput, PasswordInput, Stack, Text } from "@mantine/core";
import styled from "styled-components";

import {
  getPasskeyCredential,
  isConditionalMediationAvailable,
  isWebAuthnAvailable,
} from "@util/passkeys";

export default function LoginForm({ isAdmin, isUser }) {
  const [loading, setLoading] = useState(false);
  const [passkeyLoading, setPasskeyLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [mfaInfo, setMfaInfo] = useState(null);
  const [remember, setRemember] = useState(false);
  const conditionalAbortRef = useRef(null);

  const emailKey = `clearscreen${isAdmin ? "Admin" : "User"}Email`;
  const passkeysSupported = isWebAuthnAvailable();
  const optionsPath = isAdmin
    ? "/admin-passkey-login/options/"
    : "/user-passkey-login/options/";
  const verifyPath = isAdmin
    ? "/admin-passkey-login/verify/"
    : "/user-passkey-login/verify/";

  useEffect(() => {
    checkLocalStorage();
  }, []);

  useEffect(() => {
    if (!passkeysSupported || mfaInfo) return undefined;

    let cancelled = false;
    const abortController = new AbortController();
    conditionalAbortRef.current = abortController;

    async function startConditional() {
      const available = await isConditionalMediationAvailable();
      if (!available || cancelled) return;

      try {
        localStorage.setItem(
          "clearscreen-entry-point",
          isAdmin ? "/admin" : "/user"
        );
        const { data: optionsData } = await axios.post(optionsPath, {});
        if (cancelled) return;
        const options = optionsData.response[0];
        const credential = await getPasskeyCredential(options, {
          mediation: "conditional",
          signal: abortController.signal,
        });
        if (cancelled) return;
        await axios.post(verifyPath, {
          ceremony_id: options.ceremony_id,
          credential,
        });
        window.location = "/";
      } catch (err) {
        if (
          cancelled ||
          err?.name === "AbortError" ||
          err?.name === "NotAllowedError"
        ) {
          return;
        }
        // Conditional UI often aborts quietly when the user ignores it.
      }
    }

    startConditional();

    return () => {
      cancelled = true;
      abortController.abort();
      conditionalAbortRef.current = null;
    };
  }, [isAdmin, isUser, mfaInfo, optionsPath, passkeysSupported, verifyPath]);

  function onRememberChange() {
    const newRemember = !remember;

    setRemember(newRemember);

    if (!newRemember) {
      localStorage.removeItem(emailKey);
    } else {
      localStorage.setItem(emailKey, email);
    }
  }

  function checkLocalStorage() {
    const storedEmail = localStorage.getItem(emailKey);

    if (storedEmail) {
      setEmail(storedEmail);
      setRemember(true);
    } else {
      setRemember(false);
    }
  }

  function onSubmit(e) {
    e.preventDefault();

    if (remember) {
      localStorage.setItem(emailKey, email);
    }

    const req = {
      email,
      password,
    };

    setLoading(true);

    axios
      .post(isAdmin ? "/admin-login/" : `/user-login/`, req)
      .then(({ data }) => {
        localStorage.setItem(
          "clearscreen-entry-point",
          isAdmin ? "/admin" : "/user"
        );
        if (data.response[0] && data.response[0].mfa) {
          setLoading(false);
          setMfaInfo(data.response[0]);
        } else {
          window.location = "/";
        }
      })
      .catch((err) => {
        setLoading(false);
        toast.error(err);
      });
  }

  async function onPasskeyLogin() {
    if (!passkeysSupported) {
      toast.error("Passkeys are not supported in this browser.");
      return;
    }

    if (remember && email) {
      localStorage.setItem(emailKey, email);
    }

    if (conditionalAbortRef.current) {
      conditionalAbortRef.current.abort();
    }

    setPasskeyLoading(true);
    try {
      localStorage.setItem(
        "clearscreen-entry-point",
        isAdmin ? "/admin" : "/user"
      );

      const body = email.trim() ? { email: email.trim() } : {};
      const { data: optionsData } = await axios.post(optionsPath, body);
      const options = optionsData.response[0];
      const credential = await getPasskeyCredential(options);
      await axios.post(verifyPath, {
        ceremony_id: options.ceremony_id,
        credential,
      });
      window.location = "/";
    } catch (err) {
      if (err?.name === "NotAllowedError") {
        toast.error("Passkey prompt was cancelled.");
      } else {
        toast.error(typeof err === "string" ? err : err?.message || err);
      }
    } finally {
      setPasskeyLoading(false);
    }
  }

  if (mfaInfo) {
    return (
      <MFA
        phoneId={mfaInfo.phone_id}
        adminId={mfaInfo.admin_id}
        userId={mfaInfo.user_id}
        passkeyAvailable={!!mfaInfo.passkey_available}
      />
    );
  }

  return (
    <StyledForm onSubmit={onSubmit}>
      <TextInput
        type="email"
        name="username"
        autoComplete="username webauthn"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="email"
        size="md"
        style={{ marginBottom: "10px" }}
      />
      <PasswordInput
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        placeholder="password"
        size="md"
        autoComplete="current-password"
      />
      <Button
        type="submit"
        disabled={!email || !password || loading || passkeyLoading}
        onClick={onSubmit}
      >
        Submit
      </Button>
      {passkeysSupported && (
        <Button
          type="button"
          variant="light"
          disabled={loading || passkeyLoading}
          loading={passkeyLoading}
          onClick={onPasskeyLogin}
        >
          Sign in with passkey
        </Button>
      )}
      <Checkbox
        label="Remember me"
        checked={remember}
        onChange={onRememberChange}
      />
    </StyledForm>
  );
}

LoginForm.defaultProps = {
  isAdmin: false,
};

function MFA({ phoneId, adminId, userId, passkeyAvailable }) {
  const [code, setCode] = useState("");
  const [passkeyLoading, setPasskeyLoading] = useState(false);
  const passkeysSupported = isWebAuthnAvailable();

  function onSubmit(e) {
    e.preventDefault();

    const req = {
      code,
      phone_id: phoneId,
    };

    if (adminId) req.admin_id = adminId;
    if (userId) req.user_id = userId;

    axios
      .post(`/verify-text-code/`, req)
      .then(() => {
        window.location = `/`;
      })
      .catch((err) => {
        toast.error(err);
      });
  }

  async function onPasskeyMfa() {
    setPasskeyLoading(true);
    try {
      const body = {};
      if (adminId) body.admin_id = adminId;
      if (userId) body.user_id = userId;

      const { data: optionsData } = await axios.post(
        "/passkey-mfa/options/",
        body
      );
      const options = optionsData.response[0];
      const credential = await getPasskeyCredential(options);
      await axios.post("/passkey-mfa/verify/", {
        ceremony_id: options.ceremony_id,
        credential,
      });
      window.location = "/";
    } catch (err) {
      if (err?.name === "NotAllowedError") {
        toast.error("Passkey prompt was cancelled.");
      } else {
        toast.error(typeof err === "string" ? err : err?.message || err);
      }
    } finally {
      setPasskeyLoading(false);
    }
  }

  return (
    <StyledForm>
      <Stack gap="sm">
        <Text size="sm">Enter the code sent to your phone</Text>
        <TextInput
          placeholder="Code"
          value={code}
          onChange={(e) => setCode(e.target.value)}
        />
        <Button disabled={!code || code.length !== 6} onClick={onSubmit}>
          Submit
        </Button>
        {passkeyAvailable && passkeysSupported && (
          <>
            <Text size="sm" ta="center" c="dimmed">
              or
            </Text>
            <Button
              type="button"
              variant="light"
              loading={passkeyLoading}
              onClick={onPasskeyMfa}
            >
              Verify with passkey
            </Button>
          </>
        )}
      </Stack>
    </StyledForm>
  );
}

const StyledForm = styled.form`
  margin: 0 auto;
  width: 300px;

  .mantine-Checkbox-labelWrapper {
    label {
      /* color: #fff; */
    }
  }

  button {
    margin-top: 10px;
    width: 100%;
  }
`;
